import React, { RefObject, useEffect, useRef, useState } from 'react'
import { Box, Flex, Grid, styled } from '@chilipiper/design-system'
import { Button, UnstyledButton } from '@chilipiper/design-system/src/new'
import * as R from 'remeda'
import { useLocale } from '@chilipiper/locale/src/new'
import { IconChevronBottom, IconChevronTop } from '@chilipiper/icons/figma-generated/src/icon'
import { getSlotLabel } from '@chilipiper/booking-flow'
import { useTranslations } from '../../../context/TranslationContext'
import { chatPublicEventAction, chiliChatEventName } from '../../../constants'
import { customerColorCss } from './styles'

export interface TimesEvents {
  scrollIntoView(): void
}

interface Props {
  eventsRef?: RefObject<TimesEvents | null | undefined>
  onTimeChange: (time: string) => void
  startTimes?: Array<string>
  time?: string
  suggestedTimesToggle?: React.ReactNode
  shouldSubmitFormOnChange?: boolean
  timezone: string
  cancelRescheduling?: () => void
}

export const Times = ({
  onTimeChange,
  startTimes,
  time,
  eventsRef,
  suggestedTimesToggle,
  shouldSubmitFormOnChange,
  timezone,
  cancelRescheduling,
}: Props) => {
  const timeSlotsRef = useRef<HTMLDivElement>(null)
  const locale = useLocale()
  useEffect(() => {
    if (eventsRef) {
      eventsRef.current = {
        scrollIntoView: () => {
          setTimeout(
            () => timeSlotsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
            0
          )
        },
      }
      return () => (eventsRef.current = null)
    }
    return R.doNothing()
  }, [eventsRef])

  const [cancelReschedulingText] = useTranslations(['cancelRescheduling'])
  const [showMore, setShowMore] = useState(false)
  const startTimesToShow = showMore ? startTimes : startTimes?.slice(0, 2)
  useEffect(() => {
    if (startTimesToShow?.length === 0) {
      window.dispatchEvent(
        new CustomEvent(chiliChatEventName, {
          detail: { action: chatPublicEventAction.NO_FREE_SLOTS },
        })
      )
    }
  }, [startTimesToShow])
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(chiliChatEventName, {
        detail: {
          action: chatPublicEventAction.AVAILABILITY_LOADED,
          args: { availableSlots: startTimesToShow?.length },
        },
      })
    )
  }, [])
  const showMoreButton = Boolean(startTimes?.length && startTimes?.length > 2)

  return (
    <Box px={5}>
      <Grid rowGap={3} columnGap={2} gridTemplateColumns='1fr 1fr' py={5} ref={timeSlotsRef}>
        {startTimesToShow?.map(startTime => {
          const { hour, period } = getSlotLabel(new Date(startTime), locale, timezone)

          return (
            <TimeButton
              key={startTime}
              bg='bg/button-moderate-default'
              color='fg/major'
              borderRadius={10}
              py='13px'
              textAlign='center'
              fontSize={14}
              fontWeight={600}
              onClick={() => onTimeChange(startTime)}
              isSelected={startTime === time}
              type={shouldSubmitFormOnChange ? 'submit' : 'button'}
            >
              {`${hour} ${period}`}
            </TimeButton>
          )
        })}
      </Grid>
      {showMoreButton && (
        <ShowMoreOrSuggestedTimesToggle
          showMore={startTimes?.length !== startTimesToShow?.length}
          onShowMoreClick={() => setShowMore(!showMore)}
          suggestedTimesToggle={suggestedTimesToggle}
        />
      )}
      {!!cancelRescheduling && (
        <Box mb={5}>
          <Button isFullWidth variant='minor' onClick={cancelRescheduling}>
            {cancelReschedulingText ?? 'Cancel rescheduling'}
          </Button>
        </Box>
      )}
    </Box>
  )
}

const ShowMoreOrSuggestedTimesToggle = ({
  showMore,
  onShowMoreClick,
  suggestedTimesToggle,
}: {
  showMore: boolean
  onShowMoreClick: () => void
  suggestedTimesToggle: React.ReactNode
}) => {
  const [showMoreText, showLessText] = useTranslations(['showMore', 'showLess'])
  if (showMore || suggestedTimesToggle) {
    return (
      <ShowMoreContainer>
        <Button
          isFullWidth
          startContent={
            <Button.StartContent
              variant='icon'
              icon={showMore ? IconChevronBottom : IconChevronTop}
            />
          }
          variant='minor'
          onClick={onShowMoreClick}
        >
          {showMore ? (showMoreText ?? 'Show more slots') : (showLessText ?? 'Show less slots')}
        </Button>
      </ShowMoreContainer>
    )
  }
  if (suggestedTimesToggle) {
    return <ShowMoreContainer>{suggestedTimesToggle}</ShowMoreContainer>
  }
  return null
}

const ShowMoreContainer = styled(Flex)`
  justify-content: center;
  padding: ${({ theme }) => theme.space[4]} 0;
  box-shadow: ${({ theme }) => theme.shadows['border/row-top']};
`

const TimeButton = styled(UnstyledButton)<{ isSelected: boolean }>`
  transition:
    background-color 0.2s,
    color 0.2s;

  ${({ isSelected }) => (isSelected ? customerColorCss : undefined)}
  :hover, :focus {
    ${customerColorCss}
  }
`
